import { dentalPageSections, getDentalPageProgress } from "./dentalPageProgress";
import { getFastTrackerReporter } from "./fastTracker";

export const dentalSectionDurationEvent = "dental_section_view_duration";
type FlushReason = "section_change" | "pagehide" | "route_leave";
type Reporter = ReturnType<typeof getFastTrackerReporter>;

export function installDentalSectionAnalytics(main: HTMLElement, reportOverride?: Reporter) {
  const document = main.ownerDocument;
  const browser = document.defaultView;
  if (!browser) return () => {};
  const report = reportOverride ?? getFastTrackerReporter(browser);
  const sections = dentalPageSections.flatMap((definition, index) => {
    const element = main.querySelector<HTMLElement>(`:scope > section[id="${definition.id}"]`);
    return element ? [{ ...definition, index: index + 1, element }] : [];
  });
  if (!sections.length) return () => {};

  // A page visit identifier is not persisted and contains no user information.
  const pageViewId = browser.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const pagePath = browser.location.pathname;
  const totals = new Map<string, number>();
  let sequence = 0;
  let active: (typeof sections)[number] | null = null;
  let lastAt: number | null = null;
  let pendingDuration = 0;
  let frame = 0;
  let suspended = false;
  let disposed = false;
  const now = () => Math.floor(browser.performance.now());
  const visible = () => !disposed && !suspended && document.visibilityState === "visible" && main.isConnected;

  const accrue = () => {
    const time = now();
    if (active && lastAt !== null) pendingDuration += Math.max(0, time - lastAt);
    if (lastAt !== null) lastAt = time;
  };

  const flush = (reason: FlushReason) => {
    accrue();
    const duration = pendingDuration;
    pendingDuration = 0;
    if (!active || duration <= 0) return;
    const total = (totals.get(active.id) ?? 0) + duration;
    totals.set(active.id, total);
    sequence += 1;
    try {
      report(dentalSectionDurationEvent, {
        schema_version: 1,
        page_path: pagePath,
        page_view_id: pageViewId,
        event_id: `${pageViewId}:${sequence}`,
        sequence,
        section_id: active.id,
        section_name: active.label,
        section_index: active.index,
        duration_ms: duration,
        section_total_ms: total,
        reason,
      }, "dental", reason !== "section_change");
    } catch {
      // Analytics must never affect scrolling, navigation, or page cleanup.
    }
  };

  const update = () => {
    if (!visible()) return;
    const bounds = sections.map(({ element }) => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top + browser.scrollY, bottom: rect.bottom + browser.scrollY };
    });
    const { activeIndex } = getDentalPageProgress(
      bounds, browser.scrollY, browser.innerHeight, document.documentElement.scrollHeight,
    );
    const boundsAtIndex = bounds[activeIndex];
    // Use the same reading position as the page navigation, but never count
    // a missing/zero-height section or one outside the viewport.
    const next = boundsAtIndex && boundsAtIndex.bottom > boundsAtIndex.top
      && boundsAtIndex.bottom > browser.scrollY
      && boundsAtIndex.top < browser.scrollY + browser.innerHeight ? sections[activeIndex] : null;
    if (active !== next) {
      flush("section_change");
      active = next;
    }
    lastAt = active ? lastAt ?? now() : null;
  };

  const schedule = () => {
    if (visible() && !frame) frame = browser.requestAnimationFrame(() => { frame = 0; update(); });
  };
  const pause = () => {
    accrue();
    lastAt = null;
    browser.cancelAnimationFrame(frame);
    frame = 0;
  };
  const start = () => {
    if (!visible()) return;
    update();
  };
  const onVisibility = () => document.visibilityState === "visible" ? start() : pause();
  const onPageHide = () => { suspended = true; pause(); flush("pagehide"); };
  const onFreeze = () => { suspended = true; pause(); };
  const onResume = () => { suspended = false; start(); };

  start();
  browser.addEventListener("scroll", schedule, { passive: true });
  browser.addEventListener("resize", schedule);
  browser.addEventListener("pagehide", onPageHide);
  browser.addEventListener("pageshow", onResume);
  document.addEventListener("visibilitychange", onVisibility);
  document.addEventListener("freeze", onFreeze);
  document.addEventListener("resume", onResume);
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
  observer?.observe(main);
  sections.forEach(({ element }) => observer?.observe(element));

  return () => {
    if (disposed) return;
    pause();
    flush("route_leave");
    disposed = true;
    browser.removeEventListener("scroll", schedule);
    browser.removeEventListener("resize", schedule);
    browser.removeEventListener("pagehide", onPageHide);
    browser.removeEventListener("pageshow", onResume);
    document.removeEventListener("visibilitychange", onVisibility);
    document.removeEventListener("freeze", onFreeze);
    document.removeEventListener("resume", onResume);
    observer?.disconnect();
  };
}
